'use client'

import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import { formatPriceToNumber, useMediaQuery } from '@segway/core'
import Big from 'big.js'

import { Link } from '@/i18n/navigation'
import reportGTMEvent from '@/utils/gtm'

import { ModuleTitle } from '../moduleTtile'
import { ToggleButton } from '../toggleButton'
import { ProductMoreType } from '../types'
import { SavePrice } from '../'

type MoreProductProps = {
  blockData: ProductMoreType
  identify: string
}

const MoreProduct = ({ blockData, identify }: MoreProductProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(1)
  const responsive = useMediaQuery()

  const isXlScreen = responsive && responsive.xl
  const isMdScreen = responsive && responsive.md

  const handleClick = (button: React.SetStateAction<number>) => {
    setSelectedIndex(button)
  }

  /**
   * GTM:
   */
  useEffect(() => {
    if (blockData?.products?.length) {
      const products = [
        blockData?.products[0].first_product ?? [],
        blockData?.products[0].second_product ?? [],
        blockData?.products[0].third_product ?? [],
      ]
      const items = products.map((item, index) => {
        const item_category = item.gtm_item_categories
          ? JSON.parse(item.gtm_item_categories)
          : {
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
            }

        return {
          item_id: item.sku,
          item_name: item.name,
          msrp: formatPriceToNumber(item.price).numberValue,
          price: formatPriceToNumber(item.final_price).numberValue,
          index: index + 1,
          item_brand: 'Segway',
          ...item_category,
        }
      })

      reportGTMEvent({
        event: 'view_item_list',
        ecommerce: {
          item_list_name: identify,
          items,
        },
      })
    }
  }, [blockData, identify])

  /**
   * GTM: select_item
   */
  const handleReportProductGTMEvent = useCallback(
    (data: MoreProductProps['blockData']['products'][number]['first_product'], index: number) => {
      return () => {
        const item_category = data.gtm_item_categories
          ? JSON.parse(data.gtm_item_categories)
          : {
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
            }

        reportGTMEvent({
          event: 'select_item',
          ecommerce: {
            item_list_name: identify,
            items: [
              {
                item_id: data.sku,
                item_name: data.name,
                msrp: formatPriceToNumber(data.price).numberValue,
                price: formatPriceToNumber(data.final_price).numberValue,
                item_brand: 'Segway',
                index,
                ...item_category,
              },
            ],
          },
        })
      }
    },
    [identify],
  )

  return (
    <div className="flex w-full items-center justify-center px-7 max-xl:px-4">
      <div className="flex w-full flex-col 2xl:max-w-[1440px]">
        <ModuleTitle title="More Product" viewAll="" />
        <div className="flex flex-col">
          {blockData.products.length > 1 ? (
            <div className="pb-[32px] max-xl:pb-[20px]">
              <ToggleButton
                selectedIndex={selectedIndex}
                handleClick={handleClick}
                data={blockData}
              />
            </div>
          ) : null}
          <div>
            {blockData?.products?.map((item, index: number) => (
              <div
                className={`grid grid-cols-4 gap-[16px] ${selectedIndex === index + 1 ? '' : 'hidden'} max-xl:grid-cols-2`}
                key={index}>
                <div className="relative col-span-2">
                  <Link
                    className="relative flex overflow-hidden rounded-[12px] bg-[#F3F3F4] pt-[64%] max-xl:pt-[40.816%] xl:rounded-[20px]"
                    href={item?.first_product?.url_key}
                    onClick={handleReportProductGTMEvent(item?.first_product, 1)}>
                    <div className="absolute top-0 h-full w-full">
                      <Image
                        src={
                          isXlScreen || isMdScreen ? item?.pc_image || '' : item?.mob_image || ''
                        }
                        fill
                        style={{ objectFit: 'cover' }}
                        alt={`${item?.first_product?.image_alt}`}
                      />
                    </div>
                    <div className="absolute bottom-0 flex w-[100%] flex-col gap-[11px] px-[20px] pb-[24px] font-miSansDemiBold450 max-md:bottom-[unset] max-md:top-0 max-md:pb-[24px] max-md:pl-[20px] max-md:pr-[24px] max-md:pt-[18px]">
                      <div className="flex flex-col gap-[8px] max-md:gap-1">
                        <p
                          className="h-[17px] overflow-hidden text-ellipsis whitespace-nowrap font-miSansRegular330 text-[14px] leading-[17px] max-md:order-2 max-md:h-6 max-md:text-[14px] max-md:leading-[20px] xl:font-miSansDemiBold450"
                          style={{ color: item?.sub_text_color }}>
                          {item?.first_product?.sw_simple_description}
                        </p>
                        <p
                          className={`${isXlScreen || isMdScreen ? 'overflow-hidden text-ellipsis whitespace-nowrap' : 'max-w-[170px]'} text-[20px] leading-[24px] max-md:order-1 max-md:line-clamp-2 max-md:h-fit max-md:font-miSansMedium380 max-md:text-[18px] max-md:leading-[26px]`}
                          style={{ color: item?.text_color }}>
                          {item?.first_product?.name}
                        </p>
                      </div>
                      {(isXlScreen || isMdScreen) && (
                        <div className="flex h-[22px] gap-[12px] text-black max-xl:h-[17px]">
                          <p
                            className={`flex items-baseline justify-center text-[16px] text-[#DA291C] ${Big(item?.first_product?.price).minus(item?.first_product?.final_price).toNumber() > 0 ? 'text-[#DA291C]' : ''}`}>
                            <span className="mr-[3px] text-[12px] leading-[1] xl:text-[14px]">
                              $
                            </span>
                            {item?.first_product?.final_price}
                          </p>
                          {Big(item?.first_product?.price)
                            .minus(item?.first_product?.final_price)
                            .toNumber() > 0 ? (
                            <p className="text-decoration-line: flex items-baseline justify-center text-[16px] text-[#86868B] line-through">
                              <span className="mr-[3px] text-[12px] leading-[1] xl:text-[14px]">
                                $
                              </span>
                              {item?.first_product?.price}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
                <Link
                  className="relative overflow-hidden max-xl:border-none max-xl:bg-[unset]"
                  href={item?.second_product?.url_key}
                  onClick={handleReportProductGTMEvent(item?.second_product, 2)}>
                  <SavePrice
                    originPrice={item?.second_product?.price}
                    finalPrice={item?.second_product?.final_price}
                  />
                  <div className="bg-[#F3F3F4] max-xl:rounded-[12px] xl:rounded-[20px]">
                    <Image
                      src={item?.second_product?.image}
                      width={348}
                      height={348}
                      className="aspect-square max-w-full duration-300 lg:hover:scale-110"
                      alt={item?.second_product?.image_alt || ''}
                    />
                  </div>
                  <div className="flex w-[100%] flex-col gap-[12px] px-[0px] pb-0 pt-[16px] font-miSansDemiBold450 max-xl:px-1 max-xl:pb-0 max-xl:pt-3">
                    <div className="flex flex-col gap-[8px] px-1 max-xl:gap-[6px]">
                      <p className="h-[17px] overflow-hidden text-ellipsis whitespace-nowrap text-[14px] leading-[17px] text-[#6E6E73] max-xl:h-3 max-xl:text-[10px] max-xl:leading-[12px]">
                        {item?.second_product?.sw_simple_description}
                      </p>
                      <p className="h-7 overflow-hidden text-ellipsis whitespace-nowrap font-miSansMedium380 text-[20px] leading-[24px] text-[#000000] max-xl:line-clamp-2 max-xl:h-[34px] max-xl:whitespace-normal max-xl:text-[14px] max-xl:leading-[17px] xl:font-miSansDemiBold450">
                        {item?.second_product?.name}
                      </p>
                    </div>
                    <div className="flex h-[22px] gap-[12px] text-black max-xl:h-[17px]">
                      <p
                        className={`flex items-baseline justify-center px-1 text-[16px] max-xl:text-[14px] ${Big(item?.second_product?.price).minus(item?.second_product?.final_price).toNumber() > 0 ? 'text-[#DA291C]' : ''}`}>
                        <span className="mr-[3px] text-[12px] leading-[1] xl:text-[14px]">$</span>
                        {item?.second_product?.final_price}
                      </p>
                      {Big(item?.second_product?.price)
                        .minus(item?.second_product?.final_price)
                        .toNumber() > 0 ? (
                        <p className="text-decoration-line: flex items-baseline justify-center px-1 text-[16px] text-[#86868B] line-through max-xl:text-[14px]">
                          <span className="mr-[3px] text-[12px] leading-[1] xl:text-[14px]">$</span>
                          {item?.second_product?.price}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Link>
                <Link
                  className="relative overflow-hidden max-xl:border-none max-xl:bg-[unset]"
                  href={item?.third_product?.url_key}
                  onClick={handleReportProductGTMEvent(item?.third_product, 3)}>
                  <SavePrice
                    originPrice={item?.third_product?.price}
                    finalPrice={item?.third_product?.final_price}
                  />
                  <div className="rounded-[20px] bg-[#F3F3F4] max-xl:rounded-[12px]">
                    <Image
                      src={item?.third_product?.image}
                      width={348}
                      height={348}
                      className="aspect-square max-w-full duration-300 lg:hover:scale-110"
                      alt={item?.third_product?.image_alt || ''}
                    />
                  </div>
                  <div className="flex w-[100%] flex-col gap-[12px] px-[0px] pb-0 pt-[16px] font-miSansDemiBold450 max-xl:px-1 max-xl:pb-0 max-xl:pt-3">
                    <div className="flex flex-col gap-[8px] px-1 max-xl:gap-[6px]">
                      <p className="h-[17px] overflow-hidden text-ellipsis whitespace-nowrap text-[14px] leading-[17px] text-[#6E6E73] max-xl:h-3 max-xl:text-[10px] max-xl:leading-[12px]">
                        {item?.third_product?.sw_simple_description}
                      </p>
                      <p className="h-7 overflow-hidden text-ellipsis whitespace-nowrap font-miSansMedium380 text-[20px] leading-[24px] text-[#000000] max-xl:line-clamp-2 max-xl:h-[34px] max-xl:whitespace-normal max-xl:text-[14px] max-xl:leading-[17px] xl:font-miSansDemiBold450">
                        {item?.third_product?.name}
                      </p>
                    </div>
                    <div className="flex h-[22px] gap-[12px] text-black max-xl:h-[17px]">
                      <p
                        className={`flex items-baseline justify-center px-1 text-[16px] max-xl:text-[14px] max-xl:leading-[22px] ${Big(item?.third_product?.price).minus(item?.third_product?.final_price).toNumber() > 0 ? 'text-[#DA291C]' : ''}`}>
                        <span className="mr-[3px] text-[12px] leading-[1] xl:text-[14px]">$</span>
                        {item?.third_product?.final_price}
                      </p>
                      {Big(item?.third_product?.price)
                        .minus(item?.third_product?.final_price)
                        .toNumber() > 0 ? (
                        <p className="text-decoration-line: flex items-baseline justify-center px-1 text-[16px] text-[#86868B] line-through max-xl:text-[14px] max-xl:leading-[22px]">
                          <span className="mr-[3px] text-[12px] leading-[1] xl:text-[14px]">$</span>
                          {item?.third_product?.price}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MoreProduct
